import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, FileText, Presentation, Link2, Copy, History, Save, RotateCcw, CheckCircle2, Rocket, ExternalLink, Palette, Upload, Trash2 } from "lucide-react";
import { sr } from "../../lib/sr";

const field = { width: "100%", border: "1px solid var(--border)", borderRadius: 8, padding: "7px 9px", fontSize: 13, background: "#fff" };

function Section({ title, icon, right, children }) {
  return (
    <div className="sr-card" style={{ marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        {icon}<div style={{ fontWeight: 700, fontSize: 15, flex: 1 }}>{title}</div>{right}
      </div>
      {children}
    </div>
  );
}

const PRESETS = [
  { name: "Gold", hex: "#C8A248" }, { name: "Navy", hex: "#0F1A3C" },
  { name: "Emerald", hex: "#0E7C5A" }, { name: "Crimson", hex: "#B42318" }, { name: "Slate", hex: "#475569" },
];

function AssetUploader({ pid, prefix, label, hint, initialName, initialHas, fetchBlob, upload, remove }) {
  const [hasAsset, setHasAsset] = useState(!!initialHas);
  const [name, setName] = useState(initialName || null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => {
    let url;
    if (initialHas) {
      fetchBlob(pid).then((blob) => { url = URL.createObjectURL(blob); setPreview(url); }).catch(() => {});
    }
    return () => { if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line
  }, [pid, initialHas]);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Upload a PNG or JPG image"); return; }
    setBusy(true);
    try {
      const r = await upload(pid, f);
      setHasAsset(true); setName(r.theme[`${prefix}_name`]); setPreview(URL.createObjectURL(f));
      toast.success(`${label} uploaded`);
    } catch (err) { toast.error(err?.response?.data?.detail || `Couldn't upload ${label.toLowerCase()}`); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const onRemove = async () => {
    setBusy(true);
    try { await remove(pid); setHasAsset(false); setName(null); setPreview(""); toast.success(`${label} removed`); }
    catch { toast.error(`Couldn't remove ${label.toLowerCase()}`); } finally { setBusy(false); }
  };
  const isCover = prefix === "cover";
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--navy)" }}>{label}</div>
      <div className="sr-muted" style={{ fontSize: 11, marginBottom: 6 }}>{hint}</div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        {hasAsset && preview ? (
          <img src={preview} alt={label} data-testid={`${prefix}-preview`}
            style={{ height: isCover ? 46 : 40, width: isCover ? 150 : "auto", maxWidth: isCover ? 150 : 150, objectFit: isCover ? "cover" : "contain", background: "#F8FAFC", borderRadius: 6, padding: isCover ? 0 : 4, border: "1px solid var(--border)" }} />
        ) : null}
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: "none" }} onChange={onFile} data-testid={`${prefix}-input`} />
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`upload-${prefix}`}>
          {busy ? <Loader2 size={13} className="sr-spin" /> : <Upload size={13} />} {hasAsset ? `Replace ${label.toLowerCase()}` : `Upload ${label.toLowerCase()}`}
        </button>
        {hasAsset ? <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={onRemove} disabled={busy} data-testid={`remove-${prefix}`}><Trash2 size={13} /> Remove</button> : null}
        {name ? <span className="sr-muted" style={{ fontSize: 11.5 }}>{name}</span> : null}
      </div>
    </div>
  );
}

function ThemePanel({ pid, proposal }) {
  const theme = proposal?.theme || {};
  const [accent, setAccent] = useState(theme.accent_color || null);
  const [busy, setBusy] = useState(false);

  const pick = async (hex) => {
    setBusy(true);
    try { const r = await sr.proposalThemeSave(pid, hex); setAccent(r.theme.accent_color); toast.success(hex ? "Accent applied" : "Reverted to brand gold"); }
    catch { toast.error("Couldn't save accent"); } finally { setBusy(false); }
  };
  const current = (accent || "#C8A248").toUpperCase();

  return (
    <Section title="Deck theming" icon={<Palette size={18} />}>
      <div className="sr-muted" style={{ fontSize: 12, marginBottom: 10 }}>Accent colour, client logo and a cover image — applied to the PDF, the PPTX and the shareable page.</div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }} data-testid="theme-swatches">
        {PRESETS.map((p) => {
          const active = current === p.hex.toUpperCase();
          return (
            <button key={p.hex} onClick={() => pick(p.hex)} disabled={busy} data-testid={`swatch-${p.name.toLowerCase()}`} title={p.name}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, border: active ? "2px solid var(--navy)" : "1px solid var(--border)", borderRadius: 999, padding: active ? "3px 10px 3px 4px" : "4px 11px 4px 5px", background: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
              <span style={{ width: 18, height: 18, borderRadius: 999, background: p.hex, border: "1px solid rgba(0,0,0,.12)" }} />
              {p.name}{active ? <CheckCircle2 size={13} style={{ color: "var(--navy)" }} /> : null}
            </button>
          );
        })}
        {accent ? <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => pick("")} disabled={busy} data-testid="swatch-default">Reset</button> : null}
      </div>
      <AssetUploader pid={pid} prefix="logo" label="Client logo" hint="Shown top-right of the deck & PDF. PNG/JPG, under 3 MB."
        initialName={theme.logo_name} initialHas={!!theme.logo_file_id}
        fetchBlob={sr.proposalLogo} upload={sr.proposalLogoUpload} remove={sr.proposalLogoRemove} />
      <AssetUploader pid={pid} prefix="cover" label="Cover image" hint="Hero banner on the cover & public page. Landscape PNG/JPG, under 6 MB."
        initialName={theme.cover_name} initialHas={!!theme.cover_file_id}
        fetchBlob={sr.proposalCover} upload={sr.proposalCoverUpload} remove={sr.proposalCoverRemove} />
    </Section>
  );
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export default function ProposalDeliverables({ pid, proposal, onConverted }) {
  const stage = proposal?.stage;
  const converted = proposal?.converted;
  const prospectName = (proposal?.prospect?.name || "proposal").replace(/[^A-Za-z0-9]+/g, "_");
  const isDraft = stage === "draft";

  const [busy, setBusy] = useState("");
  const [versions, setVersions] = useState([]);
  const [label, setLabel] = useState("");
  const [shareUrl, setShareUrl] = useState("");

  useEffect(() => {
    sr.proposalVersions(pid).then((r) => setVersions(r.versions || [])).catch(() => {});
    if (proposal?.share_token) {
      setShareUrl(`${window.location.origin}/p/${proposal.share_token}`);
    }
    // eslint-disable-next-line
  }, [pid, proposal?.share_token]);

  const getDoc = async (kind) => {
    if (isDraft) { toast.error("Move the proposal to 'Ready for review' first."); return; }
    setBusy(kind);
    try {
      if (kind === "pdf") {
        const blob = await sr.proposalOnepagerPdf(pid);
        downloadBlob(blob, `${prospectName}_proposal.pdf`);
      } else {
        const blob = await sr.proposalPptx(pid);
        downloadBlob(blob, `${prospectName}_proposal.pptx`);
      }
      toast.success(kind === "pdf" ? "One-page PDF downloaded" : "PPTX deck downloaded");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't generate the file";
      toast.error(typeof msg === "string" ? msg : "Couldn't generate the file");
    } finally { setBusy(""); }
  };

  const snapshot = async () => {
    setBusy("snap");
    try { const r = await sr.proposalSnapshot(pid, label.trim() || null); setVersions(r.versions || []); setLabel(""); toast.success("Version saved"); }
    catch (e) { toast.error("Couldn't save version"); } finally { setBusy(""); }
  };
  const restore = async (vid) => {
    if (!window.confirm("Restore this version? Your current plan edits will be replaced.")) return;
    setBusy("restore");
    try { await sr.proposalRestoreVersion(pid, vid); toast.success("Version restored — reloading"); onConverted && onConverted(); }
    catch (e) { toast.error("Couldn't restore"); } finally { setBusy(""); }
  };

  const makeShare = async () => {
    setBusy("share");
    try { const r = await sr.proposalShareLink(pid); setShareUrl(r.share_url); toast.success("Shareable link ready"); }
    catch (e) { toast.error("Couldn't create link"); } finally { setBusy(""); }
  };
  const copyShare = () => { navigator.clipboard?.writeText(shareUrl); toast.success("Link copied"); };
  const revokeShare = async () => {
    setBusy("revoke");
    try { await sr.proposalRevokeShare(pid); setShareUrl(""); toast.success("Link revoked"); }
    catch (e) { toast.error("Couldn't revoke"); } finally { setBusy(""); }
  };

  const accept = async () => {
    if (!window.confirm("Accept this proposal and start onboarding? This creates the client account and emails the contact an invite. No billing is triggered.")) return;
    setBusy("accept");
    try {
      const r = await sr.proposalAccept(pid);
      if (r.already_converted) toast.info("Already converted to a client.");
      else toast.success(`Accepted — client created${r.user_created ? (r.invite_sent ? ", invite emailed" : ", invite created") : ""}. Awaiting manager assignment.`);
      onConverted && onConverted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't accept proposal");
    } finally { setBusy(""); }
  };

  return (
    <div data-testid="proposal-deliverables" style={{ gridColumn: "1 / -1" }}>
      <Section title="Deliverables" icon={<FileText size={18} />}>
        {isDraft && <div className="sr-muted" style={{ fontSize: 12, marginBottom: 8 }} data-testid="deliverables-gate">Set the stage to <b>Ready for review</b> (or beyond) to generate client-ready collateral.</div>}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="sr-btn sr-btn-navy" onClick={() => getDoc("pdf")} disabled={!!busy || isDraft} data-testid="dl-onepager-pdf">
            {busy === "pdf" ? <Loader2 size={14} className="sr-spin" /> : <FileText size={14} />} One-page PDF
          </button>
          <button className="sr-btn sr-btn-navy" onClick={() => getDoc("pptx")} disabled={!!busy || isDraft} data-testid="dl-presentation-pptx">
            {busy === "pptx" ? <Loader2 size={14} className="sr-spin" /> : <Presentation size={14} />} PPTX deck
          </button>
        </div>
        <div className="sr-muted" style={{ fontSize: 10.5, marginTop: 8 }}>Downloading does not mark the proposal as shared. Internal supplier names &amp; costs are never included.</div>
      </Section>

      <ThemePanel pid={pid} proposal={proposal} />

      <Section title="Shareable proposal link" icon={<Link2 size={18} />}>
        {!shareUrl ? (
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={makeShare} disabled={!!busy} data-testid="create-share-link">
            {busy === "share" ? <Loader2 size={13} className="sr-spin" /> : <Link2 size={13} />} Create read-only link
          </button>
        ) : (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input readOnly value={shareUrl} style={{ ...field, flex: 1, minWidth: 240 }} data-testid="share-url" onFocus={(e) => e.target.select()} />
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={copyShare} data-testid="copy-share-link"><Copy size={13} /> Copy</button>
            <a className="sr-btn sr-btn-ghost sr-btn-sm" href={shareUrl} target="_blank" rel="noreferrer" data-testid="open-share-link"><ExternalLink size={13} /> Open</a>
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={revokeShare} disabled={!!busy} data-testid="revoke-share-link">Revoke</button>
          </div>
        )}
        <div className="sr-muted" style={{ fontSize: 10.5, marginTop: 8 }}>A public, read-only page the prospect can open — no login required.</div>
      </Section>

      <Section title="Versions" icon={<History size={18} />}
        right={<div style={{ display: "flex", gap: 6 }}>
          <input style={{ ...field, width: 160 }} placeholder="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="version-label" />
          <button className="sr-btn sr-btn-gold sr-btn-sm" onClick={snapshot} disabled={!!busy} data-testid="save-version">{busy === "snap" ? <Loader2 size={13} className="sr-spin" /> : <Save size={13} />} Save version</button>
        </div>}>
        {versions.length === 0 ? <div className="sr-muted" style={{ fontSize: 12.5 }}>No saved versions yet. Save one before sharing a major revision.</div> : (
          <div style={{ display: "grid", gap: 6 }} data-testid="versions-list">
            {versions.slice().reverse().map((v) => (
              <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, borderBottom: "1px solid var(--border)", paddingBottom: 6 }} data-testid={`version-${v.id}`}>
                <div style={{ flex: 1 }}>
                  <strong>{v.label}</strong>
                  <span className="sr-muted"> · {new Date(v.at).toLocaleString()}{v.pricing_total ? ` · $${Number(v.pricing_total).toLocaleString()}` : ""}</span>
                </div>
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => restore(v.id)} disabled={!!busy} data-testid={`restore-${v.id}`}><RotateCcw size={12} /> Restore</button>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section title="Accept & onboarding" icon={<Rocket size={18} />}>
        {converted ? (
          <div style={{ background: "#ECFDF5", border: "1px solid #A7F3D0", borderRadius: 10, padding: 12 }} data-testid="converted-banner">
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#065F46", fontWeight: 700, fontSize: 13.5 }}>
              <CheckCircle2 size={16} /> Accepted — converted to an onboarding client
            </div>
            <div className="sr-muted" style={{ fontSize: 12, marginTop: 6 }}>
              Client account created and awaiting a manager assignment by an admin. A draft campaign brief was seeded from this proposal. No billing was triggered.
            </div>
          </div>
        ) : (
          <>
            <button className="sr-btn sr-btn-gold" onClick={accept} disabled={!!busy} data-testid="accept-proposal">
              {busy === "accept" ? <Loader2 size={14} className="sr-spin" /> : <Rocket size={14} />} Accept &amp; start onboarding
            </button>
            <div className="sr-muted" style={{ fontSize: 11, marginTop: 8, lineHeight: 1.5 }}>
              Converts the prospect into an onboarding client, seeds a draft campaign brief from the plan, and emails the contact a set-password invite. The client is left <b>unassigned</b> for an admin to assign a manager. <b>No billing or subscription</b> is created.
            </div>
          </>
        )}
      </Section>
    </div>
  );
}
